import {
  CorporateActionProcessingCommandType,
  ProcessingFlagField,
  ProcessingRowField,
} from './corporate-action-processing.model'

/**
 * Registry that drives the Corporate Actions Processing tab (PVAL-25952).
 *
 * ONE source of truth per command type: the `columns` array below drives the
 * ag-grid colDefs, the Copy Headers output AND the positional mapping used by
 * Excel paste.
 *
 * SCOPE of "additive", precisely — because the looser claim ("a new command type
 * is one new column set plus one registry entry, no container changes") is
 * FALSIFIED by the code as it stands, and review caught it. What this registry
 * genuinely makes additive is the PRESENTATION layer: colDefs, the command-type
 * dropdown, Copy Headers and paste order all derive from an entry, so none of
 * them needs touching. Four things outside this file are still
 * single-command-type and DO need work for a second command type:
 *
 * - `CorporateActionsProcessingComponent.createRow()`, which enumerates this
 *   command type's fields literally;
 * - the `ProcessingRowField` union and `PROCESSING_VALIDATED_FIELDS` in
 *   `corporate-action-processing.model.ts`;
 * - `validateProcessingRow` in
 *   `validators/corporate-actions-processing-validator.ts`;
 * - `toProcessingRequestRow` in
 *   `util/corporate-actions-processing-request.util.ts`.
 *
 * Pushing row shape, validation and request mapping behind the registry entry is
 * the natural follow-up when a second command type actually arrives; doing it now
 * would be speculative generality with one entry to generalise from. Don't repeat
 * the unqualified claim in a docblock, a commit message or an MR description.
 */

export type ProcessingColumnDataType = 'string' | 'number' | 'date'

export interface ProcessingColumn {
  field: ProcessingRowField
  headerName: string
  dataType: ProcessingColumnDataType
  /**
   * `false` only for the optional second-destination columns. Purely
   * informational for the UI (column header hinting); the authoritative rules
   * live in `validators/corporate-actions-processing-validator.ts`.
   */
  required: boolean
  width?: number
}

/**
 * A boolean flag rendered as a checkbox column.
 *
 * Held in a SEPARATE array from `columns` on purpose. `columns` is the positional
 * contract for Excel paste and Copy Headers; a checkbox is not something a user
 * pastes from a spreadsheet, and appending it to `columns` would either widen
 * `ProcessingRowField` (breaking `Record<ProcessingRowField, string>` and
 * `isRowEmpty` — see `ProcessingFlagField`) or silently shift paste indices.
 */
export interface ProcessingFlagColumn {
  field: ProcessingFlagField
  headerName: string
  /**
   * Header tooltip copy. Omitted falls back to `headerName` in
   * `buildFlagColDef`, which only reads well for a flag with no consequence
   * worth stating (PVAL-26303 #3).
   */
  headerTooltip?: string
}

export interface ProcessingCommandRegistryEntry {
  commandType: string
  label: string
  columns: ProcessingColumn[]
  /**
   * Rendered AFTER every `columns` entry and BEFORE the Submission column.
   * Optional: a command type with no boolean flags simply omits it.
   */
  flagColumns?: ProcessingFlagColumn[]
}

/**
 * Verbatim from PVAL-26303 #3's acceptance criterion. Exported so the spec can
 * assert THIS text, not merely that a tooltip exists.
 */
export const IGNORE_COST_BASIS_ADJUSTMENTS_TOOLTIP =
  'Required if the source lot has a cost basis adjustment (e.g. a writedown) — otherwise the job fails.'

/**
 * Cash / Stock Acquisition boolean flags.
 *
 * Both flags the shipped `ProcessCashStockAcquisitionRow` accepts are
 * `Boolean = false`, so omitting either entirely is still contract-valid and the
 * request util sends the declared default regardless. A flag therefore only earns
 * a checkbox column here if it buys the operator something concrete — it is an
 * editable input that changes the financial effect of a booked command.
 *
 * `ignoreCostBasisAdjustments` closes a dead end: without it the user has no way
 * to book a cost-basis-adjusted target from this grid at all — perseus refuses
 * that command and does so asynchronously, so the failure arrives as a job toast
 * rather than an inline error, and the operator has to fall back to
 * `manual.processCashandStockAcquisition`. This is the flag that makes the tab a
 * parity replacement for that fallback (PVAL-25173).
 *
 * `forceRealizeAll` is a column, and it is here on the REPORTER's authority —
 * which is the only thing that was ever missing. It was removed on an earlier
 * revision of this branch because it appeared in no line of the PVAL-25952 ticket
 * text, its acceptance criteria or the agreed spec brief, and an unrequested
 * editable input that changes the financial effect of a booked command must not
 * ship on a developer's parity argument alone. PVAL-25952's reporter then asked
 * for it explicitly in review on MR !387 — "the `forceRealizeAll` (boolean)
 * option didn't make it into the UI" — citing perseus-worker's own
 * `ProcessCashStockAcquisitionCommand` field and noting that the corresponding
 * stored proc defaults it to `false`. That is precisely the sign-off the removal
 * was pending on, so the flag is restored on the requirement owner's decision
 * rather than a developer's. Do not remove it again without the same authority.
 *
 * Restoring it is wire-format NEUTRAL for anyone who never ticks it: it defaults
 * to `false` on every new row and `toProcessingRequestRow` still sends an
 * explicit boolean (`FORCE_REALIZE_ALL_DEFAULT` when the row carries nothing), so
 * the request body is byte-identical to the pre-column one unless the operator
 * deliberately sets it. Note also that, like every flag here, it sits outside the
 * positional paste contract and Copy Headers — so an Excel-driven batch can only
 * ever send `false` for it, and setting it is a deliberate in-grid tick per row.
 *
 * Two consequences apply to every flag here, and they are the price of keeping
 * them out of `columns` (see `ProcessingFlagColumn`): none is validated (the DTO
 * defaults them), and none is in the positional paste contract or Copy Headers —
 * so an Excel-driven batch, the ticket's headline workflow, can only ever send
 * `false`. Setting one is a deliberate in-grid tick per row.
 */
export const CASH_STOCK_ACQUISITION_FLAG_COLUMNS: ProcessingFlagColumn[] = [
  {
    field: 'ignoreCostBasisAdjustments',
    headerName: 'Ignore Cost Basis Adjustments',
    headerTooltip: IGNORE_COST_BASIS_ADJUSTMENTS_TOOLTIP,
  },
  {
    field: 'forceRealizeAll',
    headerName: 'Force Realize All',
  },
]

/**
 * Cash / Stock Acquisition column set — the only entry in this cut.
 *
 * Order is load-bearing: it is the order headers are copied in and the order
 * tab-delimited Excel values are mapped to fields on paste.
 */
export const CASH_STOCK_ACQUISITION_COLUMNS: ProcessingColumn[] = [
  {
    field: 'accountId',
    headerName: 'Account ID',
    dataType: 'number',
    required: true,
    width: 100,
  },
  {
    field: 'exDate',
    headerName: 'Ex Date',
    dataType: 'date',
    required: true,
    width: 110,
  },
  {
    field: 'targetCusip',
    headerName: 'Target CUSIP',
    dataType: 'string',
    required: true,
    width: 120,
  },
  {
    field: 'cashPerShare',
    headerName: 'Cash Per Share',
    dataType: 'number',
    required: true,
    width: 120,
  },
  {
    field: 'cashReceived',
    headerName: 'Cash Received',
    dataType: 'number',
    required: true,
    width: 120,
  },
  {
    field: 'destCusip',
    headerName: 'Dest CUSIP',
    dataType: 'string',
    required: true,
    width: 120,
  },
  {
    field: 'destShareExchRate',
    headerName: 'Dest Share Exch Rate',
    dataType: 'number',
    required: true,
    width: 150,
  },
  {
    field: 'destCusipMarketPrice',
    headerName: 'Dest CUSIP Market Price',
    dataType: 'number',
    required: true,
    width: 170,
  },
  {
    field: 'destCusip2',
    headerName: 'Dest CUSIP 2',
    dataType: 'string',
    required: false,
    width: 120,
  },
  {
    field: 'destShareExchRate2',
    headerName: 'Dest Share Exch Rate 2',
    dataType: 'number',
    required: false,
    width: 160,
  },
  {
    field: 'destCusipMarketPrice2',
    headerName: 'Dest CUSIP Market Price 2',
    dataType: 'number',
    required: false,
    width: 180,
  },
  {
    field: 'notes',
    headerName: 'Notes',
    dataType: 'string',
    required: true,
    width: 220,
  },
]

export const CORPORATE_ACTION_PROCESSING_REGISTRY: ProcessingCommandRegistryEntry[] =
  [
    {
      commandType: CorporateActionProcessingCommandType.CashStockAcquisition,
      label: 'Cash / Stock Acquisition',
      columns: CASH_STOCK_ACQUISITION_COLUMNS,
      flagColumns: CASH_STOCK_ACQUISITION_FLAG_COLUMNS,
    },
  ]

/**
 * Filter the registry down to the command types `GET
 * processing/availableCommands` returned. An empty/failed response yields an
 * empty list — the tab still renders (empty dropdown + explanatory empty state
 * + disabled Submit); the nav item is never gated on network state.
 */
export function filterRegistryByAvailableCommands(
  availableCommands: string[] | undefined | null,
  registry: ProcessingCommandRegistryEntry[] = CORPORATE_ACTION_PROCESSING_REGISTRY,
): ProcessingCommandRegistryEntry[] {
  if (!availableCommands || availableCommands.length === 0) {
    return []
  }
  const available = new Set(availableCommands)
  return registry.filter(entry => available.has(entry.commandType))
}

/** Tab-delimited header line for the Copy Headers button. */
export function buildCopyHeaders(columns: ProcessingColumn[]): string {
  return columns.map(column => column.headerName).join('\t')
}
